import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { Plus, Minus, X } from '@phosphor-icons/react';
import type { MapStyleKey } from './MapboxMap';
import { useArModelBlobs } from './useArModelBlobs';
import arWallTexture from '../../imports/ar-wall-texture.png';

// Manual zoom range, expressed as a ratio of the camera distance
// model-viewer's own "auto" framing picks for the loaded model — captured
// once per model load (see baseRadius below) since that ideal distance
// differs between the 55cm and 65cm prints.
const MIN_ZOOM_RATIO = 0.45;
const MAX_ZOOM_RATIO = 2.2;
const ZOOM_STEP = 0.85;

interface ModelViewerElement extends HTMLElement {
  getCameraOrbit: () => { theta: number; phi: number; radius: number };
}

interface ArSizeOption {
  label: string;
  price: string;
}

interface ArPreviewModalProps {
  open: boolean;
  onClose: () => void;
  imageUrl: string;
  sizeOptions: readonly ArSizeOption[];
  selectedSizeIndex: number;
  onSelectSize: (index: number) => void;
  onOrder: () => void;
  location: string;
  mapStyleKey: MapStyleKey;
}

// @google/model-viewer and the three.js exporters are only needed by the
// small slice of customers who tap "View in AR" — both are dynamically
// imported here so the main bundle stays untouched for everyone else.
export function ArPreviewModal({
  open,
  onClose,
  imageUrl,
  sizeOptions,
  selectedSizeIndex,
  onSelectSize,
  onOrder,
  location,
  mapStyleKey,
}: ArPreviewModalProps) {
  const sizeLabel = sizeOptions[selectedSizeIndex]?.label ?? sizeOptions[0].label;
  // The map image is rendered asynchronously now (see useRenderedMapPreview),
  // so imageUrl is briefly empty right after opening — hold the model build
  // until it lands. The 'loading' spinner covers both waits seamlessly.
  const { status, urls } = useArModelBlobs(open && !!imageUrl, imageUrl, sizeLabel, mapStyleKey);
  const modelViewerRef = useRef<ModelViewerElement>(null);
  // The camera distance model-viewer's own "auto" framing resolves to for
  // the currently loaded model, and the (possibly zoomed) distance actually
  // in use — null until the model has loaded once, since "ideal" distance
  // depends on the model's own size.
  const [baseRadius, setBaseRadius] = useState<number | null>(null);
  const [currentRadius, setCurrentRadius] = useState<number | null>(null);

  // Reset the zoom baseline whenever a new model starts building (size
  // changed, or the modal reopened) — the old radius belongs to a model
  // that no longer exists.
  useEffect(() => {
    if (status === 'loading') {
      setBaseRadius(null);
      setCurrentRadius(null);
    }
  }, [status]);

  // Capture the auto-framed camera distance once the model's loaded, so
  // the +/- zoom buttons below have a baseline to work from for this
  // particular model. This used to also auto-trigger native AR on phones,
  // jumping straight past this page — now that the page itself has real
  // functionality (zoom, size picker, Order), mobile visitors should land
  // here same as desktop and use model-viewer's own AR button (the cube
  // icon in the viewer) when they actually want to place it on their wall.
  useEffect(() => {
    if (status !== 'ready') return;
    const el = modelViewerRef.current;
    if (!el) return;

    const handleLoad = () => {
      const orbit = el.getCameraOrbit();
      setBaseRadius(orbit.radius);
      setCurrentRadius(orbit.radius);
    };

    el.addEventListener('load', handleLoad);
    return () => el.removeEventListener('load', handleLoad);
  }, [status]);

  const handleZoomIn = () => {
    if (baseRadius === null) return;
    setCurrentRadius((r) => Math.max((r ?? baseRadius) * ZOOM_STEP, baseRadius * MIN_ZOOM_RATIO));
  };
  const handleZoomOut = () => {
    if (baseRadius === null) return;
    setCurrentRadius((r) => Math.min((r ?? baseRadius) / ZOOM_STEP, baseRadius * MAX_ZOOM_RATIO));
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            key="ar-scrim"
            className="fixed inset-0 z-40 bg-black/60"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={onClose}
            aria-hidden="true"
          />

          <motion.div
            key="ar-panel"
            role="dialog"
            aria-modal="true"
            aria-label="View in AR"
            className="fixed inset-0 z-50 flex flex-col bg-white"
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.2 }}
          >
            <div
              className="flex-1 min-h-0 overflow-y-auto flex flex-col"
              style={{
                backgroundImage: `url(${arWallTexture})`,
                backgroundSize: '300px 300px',
                backgroundRepeat: 'repeat',
              }}
            >
              {/* Fills whatever space the size-selector section below
                  doesn't need, so that section stays pinned to the bottom
                  of the viewport instead of just trailing the print at
                  whatever height it happens to render — same on a short
                  phone screen as on a tall desktop window. */}
              <div className="relative flex-1 min-h-[360px]">
                {/* Close, then manual zoom — camera-controls (drag-to-orbit)
                    stays off so the print can't be spun or tilted off the
                    wall; the +/- buttons only move the camera in/out along
                    the fixed straight-on view. Same style as the map's own
                    zoom controls for consistency. */}
                <div className="absolute top-[16px] right-[16px] z-10 flex flex-col gap-[12px]">
                  <button
                    type="button"
                    onClick={onClose}
                    aria-label="Close"
                    className="size-[48px] rounded-full border-2 border-[#404342] bg-white hover:bg-[#f9fafb] flex items-center justify-center transition-colors cursor-pointer shadow-[0px_20px_24px_0px_rgba(16,24,40,0.08),0px_8px_8px_0px_rgba(16,24,40,0.03)]"
                  >
                    <X size={22} weight="regular" className="text-[#404342]" />
                  </button>
                  <button
                    type="button"
                    onClick={handleZoomIn}
                    disabled={baseRadius === null}
                    aria-label="Zoom in"
                    className="size-[48px] rounded-full border-2 border-[#404342] bg-white hover:bg-[#f9fafb] flex items-center justify-center transition-colors cursor-pointer shadow-[0px_20px_24px_0px_rgba(16,24,40,0.08),0px_8px_8px_0px_rgba(16,24,40,0.03)] disabled:opacity-40 disabled:cursor-default"
                  >
                    <Plus size={22} weight="regular" className="text-[#404342]" />
                  </button>
                  <button
                    type="button"
                    onClick={handleZoomOut}
                    disabled={baseRadius === null}
                    aria-label="Zoom out"
                    className="size-[48px] rounded-full border-2 border-[#404342] bg-white hover:bg-[#f9fafb] flex items-center justify-center transition-colors cursor-pointer shadow-[0px_20px_24px_0px_rgba(16,24,40,0.08),0px_8px_8px_0px_rgba(16,24,40,0.03)] disabled:opacity-40 disabled:cursor-default"
                  >
                    <Minus size={22} weight="regular" className="text-[#404342]" />
                  </button>
                </div>

                {status === 'loading' && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-[12px]">
                    <div className="size-[32px] rounded-full border-2 border-[#e4e7ec] border-t-[#404342] animate-spin" />
                    <p className="font-['Roboto',sans-serif] font-normal text-[#6b7280] text-[16px]">
                      Building your {sizeLabel} preview…
                    </p>
                  </div>
                )}

                {status === 'error' && (
                  <div className="absolute inset-0 flex items-center justify-center px-[24px]">
                    <p className="font-['Roboto',sans-serif] font-normal text-[#dc2626] text-[16px] text-center">
                      Couldn't build the AR preview. Please try again.
                    </p>
                  </div>
                )}

                {status === 'ready' && urls && (
                  <model-viewer
                    ref={modelViewerRef}
                    src={urls.glbUrl}
                    ios-src={urls.usdzUrl}
                    alt={`${sizeLabel} map print of ${location}`}
                    ar
                    ar-modes="webxr scene-viewer quick-look"
                    ar-placement="wall"
                    ar-scale="fixed"
                    // model-viewer's own contact shadow is derived from the
                    // default environment's lighting and isn't directly
                    // steerable — it came out biased toward the top edge,
                    // the opposite of what we want, and the only real fix
                    // (a custom HDRI environment-image) is a lot of surface
                    // area for something this simple. Turned off in favor of
                    // a CSS drop-shadow below, which follows the model's
                    // actual on-screen silhouette and is trivial to aim.
                    shadow-intensity="0"
                    // The model is authored lying flat (see arModel.ts) so
                    // AR wall-tipping orients it correctly — point the
                    // non-AR orbit preview straight down at it by default,
                    // otherwise it reads as a flat table edge-on. Radius
                    // starts at "auto" and switches to the explicit zoomed
                    // value once the first 'load' event has captured
                    // model-viewer's own ideal framing distance.
                    camera-orbit={currentRadius !== null ? `0deg 0deg ${currentRadius}m` : '0deg 0deg auto'}
                    // model-viewer clamps camera-orbit's radius to its own
                    // auto-computed "ideal" framing distance by default —
                    // widen that clamp once we know it, so the zoom buttons
                    // above can actually move past it in either direction.
                    min-camera-orbit={baseRadius !== null ? `auto auto ${baseRadius * MIN_ZOOM_RATIO}m` : undefined}
                    max-camera-orbit={baseRadius !== null ? `auto auto ${baseRadius * MAX_ZOOM_RATIO}m` : undefined}
                    style={{
                      width: '100%',
                      height: '100%',
                      backgroundColor: 'transparent',
                      // Light from top-left → shadow falls bottom-right.
                      filter: 'drop-shadow(10px 14px 16px rgba(0,0,0,0.35))',
                    }}
                  />
                )}
              </div>

              <div className="shrink-0 flex flex-col gap-[16px] px-[24px] py-[24px] md:max-w-[414px] md:mx-auto">
                <p
                  className="font-['Roboto',sans-serif] font-normal text-[#404342] text-[16px] tracking-[3.5px] uppercase leading-[normal]"
                  style={{ fontVariationSettings: "'wdth' 100" }}
                >
                  Select map size
                </p>

                <div className="flex gap-[24px]">
                  {sizeOptions.map((size, i) => {
                    const selected = selectedSizeIndex === i;
                    return (
                      <button
                        key={size.label}
                        type="button"
                        onClick={() => onSelectSize(i)}
                        className={`relative rounded-[8px] flex-1 text-left transition-colors ${selected ? 'bg-[#404342]' : 'bg-white'}`}
                      >
                        <div aria-hidden className="absolute border-2 border-[#404342] border-solid inset-0 pointer-events-none rounded-[8px]" />
                        <div className={`flex flex-col items-start p-[24px] tracking-[3.5px] uppercase leading-[normal] ${selected ? 'text-white' : 'text-black'}`}>
                          <p
                            className="font-['Roboto',sans-serif] font-normal text-[16px] w-full"
                            style={{ fontVariationSettings: "'wdth' 100" }}
                          >
                            {size.label}
                          </p>
                          <p className="font-['Playfair_Display',serif] font-extrabold text-[40px] w-full leading-tight">
                            {size.price}
                          </p>
                        </div>
                      </button>
                    );
                  })}
                </div>

                <button
                  type="button"
                  onClick={onOrder}
                  className="w-full h-[48px] flex items-center justify-center bg-[#404342] rounded-[9999px] transition-opacity cursor-pointer"
                >
                  <p
                    className="font-['Roboto',sans-serif] font-normal text-white text-[16px] tracking-[3.5px] uppercase leading-[normal]"
                    style={{ fontVariationSettings: "'wdth' 100" }}
                  >
                    Order
                  </p>
                </button>

                <p className="font-['Roboto',sans-serif] font-normal text-[#9ca3af] text-[16px] text-center leading-relaxed">
                  On a phone, tap the AR icon in the viewer to place this on your wall at true scale.
                </p>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
